import type { BoardToolId } from "./types";

/**
 * Пробел на доске: подержал — выделяешь, стукнул дважды — сменил перо на ластик.
 *
 * Раньше пробел должен был сдвигать холст, но проверка была написана как
 * `getModifierState("Space")`, а пробел не модификатор — такая проверка
 * всегда ложна, и пробел не делал ничего. Холст по-прежнему двигают правой
 * и средней кнопкой, а пробел занят тем, чего на уроке не хватает: во время
 * рисования постоянно нужно то передвинуть написанное, то стереть.
 */

/** Сколько держать, чтобы это считалось удержанием, а не стуком. */
export const SPACE_HOLD_MS = 200;

/** За сколько должен прийти второй стук, чтобы считаться двойным. */
export const SPACE_DOUBLE_TAP_MS = 400;

/** Во что переключает двойной стук. Остальные инструменты не трогаем. */
export function toolAfterDoubleTap(tool: BoardToolId): BoardToolId | null {
  if (tool === "pen") return "eraser";
  if (tool === "eraser") return "pen";
  return null;
}

/** Второй стук пришёл достаточно быстро после первого. */
export function isDoubleTap(prevTapAt: number | null, now: number): boolean {
  if (prevTapAt === null) return false;
  const прошло = now - prevTapAt;
  return прошло >= 0 && прошло <= SPACE_DOUBLE_TAP_MS;
}

/** Нажатие продержалось достаточно, чтобы считаться удержанием. */
export function isHold(pressedAt: number, now: number): boolean {
  return now - pressedAt >= SPACE_HOLD_MS;
}

/**
 * Сколько стрелка живёт после того, как пробел отпущен.
 *
 * На ноутбуке Windows тачпад глушит касания, пока нажата клавиша: зажав
 * пробел, выделить мышью-то можно, а пальцем — нет, курсор еле ползёт.
 * Поэтому стрелка остаётся в руке и после того, как пробел отпущен: нажал,
 * отпустил, спокойно выделил — и перо вернулось само.
 */
export const SPACE_GRACE_MS = 3000;

/** Сколько ждать между действиями: выделил объект — успеваешь его подвинуть. */
export const SPACE_GRACE_TAIL_MS = 1200;

/** Возвращать перо можно, только если стрелку не сменили вручную. */
export function shouldRestoreTool(current: BoardToolId): boolean {
  return current === "select";
}
